import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import { clean } from '../../shell/copy';
import { displayCharacters, matchCharacterNames } from './scriptHelpers';

// The Characters column header search on the Scripts list (moved from the old CharacterHeaderSearch, behaviour kept):
// suggestions come from GET /api/scripts/characters (already scoped by client / writer / view by the caller); matching is
// word-prefix, case-insensitive (matchCharacterNames, which the server's ?characterPrefix= mirrors); Down / Up move the
// highlight, Enter picks, Escape closes, Tab closes and moves on. The value is free text applied as a prefix, so an
// empty field is no filter. The suggestion list is portalled to <body>: the Grid header clips anything inside it.
export default function CharacterSearch({ query, setQuery, options }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [place, setPlace] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const shown = useMemo(() => matchCharacterNames(query, displayCharacters(options)), [query, options]);
  const showList = open && query.trim() !== '';

  useEffect(() => { setHi(0); }, [query]);
  useEffect(() => {
    if (showList) listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [hi, showList]);

  // Anchored to the field's right edge (the column sits at the grid's right), at least 220px wide.
  useLayoutEffect(() => {
    if (!showList) return undefined;
    const measure = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setPlace({ top: r.bottom + 4, right: window.innerWidth - r.right, width: Math.max(220, r.width) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showList]);

  useEffect(() => {
    if (!showList) return undefined;
    const onDown = e => { if (!inputRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showList]);

  const pick = n => { setQuery(n); setOpen(false); inputRef.current?.blur(); };

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(i => Math.min(i + 1, shown.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi(i => Math.max(i - 1, 0)); return; }
    // Clamped: the suggestions can be replaced under an open list when the client / writer scope refetches.
    if (e.key === 'Enter') { e.preventDefault(); if (showList && shown.length) pick(shown[Math.min(hi, shown.length - 1)]); return; }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'Tab') setOpen(false);
  };

  return (
    <span className="ui-cluster ui-cluster--nowrap ui-grow">
      <input
        ref={inputRef}
        className="ui-input ui-grow"
        role="combobox"
        aria-label="Filter by character"
        aria-expanded={showList}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={showList && shown.length ? `${id}-opt-${Math.min(hi, shown.length - 1)}` : undefined}
        placeholder="Characters"
        title="Show scripts with a character whose name starts with this. Type to see suggestions."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {query && <Button variant="quiet" iconOnly icon={X} label="Clear the character filter" onClick={() => { setQuery(''); setOpen(false); }} />}
      {showList && place && createPortal(
        <ul className="ui ui-combo__list" role="listbox" id={`${id}-list`} ref={listRef}
          style={{ position: 'fixed', top: place.top, right: place.right, left: 'auto', width: place.width }}>
          {shown.length === 0 && <li className="ui-combo__empty">No character name starts with {query.trim()}.</li>}
          {shown.map((n, i) => (
            <li key={n} id={`${id}-opt-${i}`} data-idx={i} role="option" aria-selected={n === query} data-active={i === hi}
              className="ui-combo__opt" onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); pick(n); }}>
              {clean(n)}
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </span>
  );
}
